import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import { ShareResultsInfo } from 'src/app/models/results/share-results-info';
import { ConfigService } from 'src/app/services/config/config.service';
import { UtilityService } from 'src/app/services/utility.service';
import { WizardStepName } from 'src/app/services/wizard/wizard-step-name.enum';
import { WizardService } from 'src/app/services/wizard/wizard.service';
import { constants } from 'src/environments/constants';
import { environment } from 'src/environments/non-vendor/environment';

export interface IShareResultsDialogData {
  showConfirmationMessage: boolean;
}

@Component({
  selector: 'app-share-results-dialog',
  templateUrl: './share-results-dialog.component.html',
  styleUrls: ['./share-results-dialog.component.scss']
})
export class ShareResultsDialogComponent implements OnInit {
  firstName = new FormControl('', [Validators.required]);
  lastName = new FormControl('', [Validators.required]);
  email = new FormControl('', [Validators.required, Validators.email]);
  company = new FormControl('', [Validators.required]);
  optInToMarketing = new FormControl(true);
  title: string = "";
  emailLabel: string = "";
  emailErrorMessage: string = "";
  firstNameLabel: string = "";
  lastNameLabel: string = "";
  companyNameLabel: string = "";
  optInCheckboxLabel: string = "";
  requiredMessage: string = "";
  submitButtonText: string = "";
  cancelButtonText: string = "";
  confirmationTitle: string = "";
  confirmationMessageBody: string = "";
  confirmationClosing: string = "";
  confirmationCloseButtonText: string = "";
  includeOptInCheckbox: boolean = false;
  isNonEnglishLocale: boolean = false;

  constructor(
    private utilityService: UtilityService,
    private confSvc: ConfigService,
    public dialogRef: MatDialogRef<ShareResultsDialogComponent>,
    private wizardService: WizardService,
    @Inject(MAT_DIALOG_DATA) public data: IShareResultsDialogData) { }

  async ngOnInit(): Promise<void> {
    await this.confSvc.setup();

    this.title = this.confSvc.getString("shareResultsDialogTitle");
    this.emailLabel = this.confSvc.getString("shareResultsEmailLabel");
    this.emailErrorMessage = this.confSvc.getString("shareResultsEmailErrorMessage");
    this.firstNameLabel = this.confSvc.getString("shareResultsFirstNameLabel");
    this.lastNameLabel = this.confSvc.getString("shareResultsLastNameLabel");
    this.companyNameLabel = this.confSvc.getString("shareResultsCompanyNameLabel");
    this.requiredMessage = this.confSvc.getString("shareResultsRequiredMessage");
    this.submitButtonText = this.confSvc.getString("shareResultsSubmitButtonText");
    this.cancelButtonText = this.confSvc.getString("shareResultsCancelButtonText");
    this.confirmationTitle = this.confSvc.getString("shareResultsConfirmationTitle");
    this.confirmationMessageBody = this.confSvc.getString("shareResultsConfirmationBody");
    this.confirmationClosing = this.confSvc.getString("shareResultsConfirmationClosing");
    this.confirmationCloseButtonText = this.confSvc.getString("shareResultsConfirmationCloseButtonText");
    this.optInCheckboxLabel = this.confSvc.getString("shareResultsOptInCheckboxLabel");
    this.isNonEnglishLocale = !this.utilityService.getLocaleInfo().isEnglish;

    if (this.shouldIncludeOptInCheckbox()) {
      this.optInToMarketing.setValue(false); // defaulted to true for countries that don't require checkbox, but set to false here so the checkbox is unchecked
      this.includeOptInCheckbox = true;
    }
  }

  shouldIncludeOptInCheckbox(): boolean {
    const country: string = this.utilityService.getLocaleInfo().countryCode;
    const countriesThatRequireOptIn: string[] = this.confSvc.getStringArray("countriesThatRequireOptIn");

    return countriesThatRequireOptIn.includes(country);
  }

  onCancelClick(): void {
    this.dialogRef.close();
  }

  onShareClick(): void {
    this.wizardService.getSelectedWizardOptionsQueryString().subscribe((queryString: string) => {
      const localizedBaseUrl = this.utilityService.getLocalizedUrl(environment.appBaseUrl, environment.env);
      const resultPageUrl = `${localizedBaseUrl}?${queryString}`;

      const shareResultsInfo: ShareResultsInfo = new ShareResultsInfo(this.firstName.value, this.lastName.value, this.email.value, this.company.value, this.optInToMarketing.value, resultPageUrl);

      this.dialogRef.close(shareResultsInfo);
    });
  }
}
